import React, { useState, useEffect } from 'react'
import { 
  Upload, 
  Play, 
  Pause, 
  TestTube, 
  Eye, 
  Trash2, 
  RotateCcw,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap
} from 'lucide-react'
import { adminApi } from '../services/api'
import { QualityTier, QualityTierOverview, WorkflowVersion } from '../types'
import { useWorkflowStore } from '../stores/workflowStore'
import WorkflowUpload from '../components/WorkflowUpload'
import WorkflowCard from '../components/WorkflowCard'
import WorkflowTester from '../components/WorkflowTester'
import toast from 'react-hot-toast'

export default function WorkflowsPage() {
  // Use workflow store for centralized state management
  const {
    overview,
    workflowHistory,
    selectedTier,
    loading,
    setOverview,
    setWorkflowHistory,
    setSelectedTier,
    setLoading
  } = useWorkflowStore()
  
  // Local UI state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showTesterModal, setShowTesterModal] = useState(false)
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowVersion | null>(null)
  
  // Get workflow history for current tier
  const currentWorkflowHistory = workflowHistory[selectedTier] || []

  useEffect(() => {
    loadWorkflowsOverview()
  }, [])

  useEffect(() => {
    if (selectedTier) {
      loadWorkflowHistory(selectedTier)
    }
  }, [selectedTier])

  const loadWorkflowsOverview = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getWorkflowsOverview()
      if (response.success) {
        setOverview(response.data)
      } else {
        toast.error('Failed to load workflows overview')
      }
    } catch (error) {
      console.error('Error loading workflows overview:', error)
      toast.error('Failed to load workflows overview')
    } finally {
      setLoading(false)
    }
  }

  const loadWorkflowHistory = async (qualityTier: string) => {
    try {
      const response = await adminApi.getWorkflowHistory(qualityTier)
      if (response.success) {
        setWorkflowHistory(qualityTier, response.data)
      } else {
        toast.error('Failed to load workflow history')
      }
    } catch (error) {
      console.error('Error loading workflow history:', error)
      toast.error('Failed to load workflow history')
    }
  }

  const handleActivateWorkflow = async (workflowId: string) => {
    try {
      const response = await adminApi.activateWorkflow(workflowId)
      if (response.success) {
        toast.success('Workflow activated successfully')
        loadWorkflowsOverview()
        loadWorkflowHistory(selectedTier)
      } else {
        toast.error('Failed to activate workflow')
      }
    } catch (error) {
      console.error('Error activating workflow:', error)
      toast.error('Failed to activate workflow')
    }
  }

  const handleDeleteWorkflow = async (workflowId: string) => {
    if (!confirm('Are you sure you want to delete this workflow?')) {
      return
    }

    try {
      const response = await adminApi.deleteWorkflow(workflowId)
      if (response.success) {
        toast.success('Workflow deleted successfully')
        loadWorkflowsOverview()
        loadWorkflowHistory(selectedTier)
      } else {
        toast.error('Failed to delete workflow')
      }
    } catch (error) {
      console.error('Error deleting workflow:', error)
      toast.error('Failed to delete workflow')
    }
  }

  const handleTestWorkflow = (workflow: WorkflowVersion) => {
    setSelectedWorkflow(workflow)
    setShowTesterModal(true)
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'testing':
        return <TestTube className="h-4 w-4 text-yellow-500" />
      case 'draft':
        return <Clock className="h-4 w-4 text-gray-500" />
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'testing':
        return 'bg-yellow-100 text-yellow-800'
      case 'draft':
        return 'bg-gray-100 text-gray-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const qualityTiers = Object.values(QualityTier).filter(tier => tier !== QualityTier.TESTING)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workflow Management</h1>
          <p className="text-gray-600">Manage ComfyUI workflows for different quality tiers</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowTesterModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <TestTube className="h-4 w-4 mr-2" />
            Test Workflow
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Workflow
          </button>
        </div>
      </div>

      {/* Quality Tier Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {overview.map((tierOverview) => (
          <div
            key={tierOverview.quality_tier}
            className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
              selectedTier === tierOverview.quality_tier
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedTier(tierOverview.quality_tier)}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold capitalize">{tierOverview.quality_tier}</h3>
              {tierOverview.active_workflow && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(tierOverview.active_workflow.status)}`}>
                  {getStatusIcon(tierOverview.active_workflow.status)}
                  <span className="ml-1">{tierOverview.active_workflow.status}</span>
                </span>
              )}
            </div>
            <div className="space-y-1 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Credits:</span>
                <span className="font-medium">{tierOverview.credits}</span>
              </div>
              <div className="flex justify-between">
                <span>Processing Time:</span>
                <span className="font-medium">{tierOverview.processing_time}</span>
              </div>
              <div className="flex justify-between">
                <span>Versions:</span>
                <span className="font-medium">{tierOverview.total_versions}</span>
              </div>
            </div>
            {tierOverview.active_workflow && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500">Active: {tierOverview.active_workflow.name}</p>
                <p className="text-xs text-gray-500">v{tierOverview.active_workflow.version}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Workflow History */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Quality Workflows
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Workflow
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Version
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentWorkflowHistory.map((workflow) => (
                <tr key={workflow.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{workflow.name}</div>
                      <div className="text-sm text-gray-500">{workflow.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    v{workflow.version}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(workflow.status)}`}>
                      {getStatusIcon(workflow.status)}
                      <span className="ml-1">{workflow.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {workflow.credits}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(workflow.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {workflow.status !== 'active' && (
                        <button
                          onClick={() => handleActivateWorkflow(workflow.id)}
                          className="text-green-600 hover:text-green-900"
                          title="Activate Workflow"
                        >
                          <Play className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleTestWorkflow(workflow)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Test Workflow"
                      >
                        <TestTube className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteWorkflow(workflow.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete Workflow"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {currentWorkflowHistory.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No workflows found for {selectedTier} quality tier
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <WorkflowUpload
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            loadWorkflowsOverview()
            loadWorkflowHistory(selectedTier)
          }}
          defaultQualityTier={selectedTier}
        />
      )}

      {/* Tester Modal */}
      {showTesterModal && (
        <WorkflowTester
          isOpen={showTesterModal}
          onClose={() => {
            setShowTesterModal(false)
            setSelectedWorkflow(null)
          }}
          workflow={selectedWorkflow}
        />
      )}
    </div>
  )
}